import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef,} from '@angular/material/dialog';
import {ShowDialogsComponent} from 'src/app/components/show-dialogs/show-dialogs.component';
import {SharedFeaturesService} from 'src/app/services/shared-features.service';
import {isEqual} from 'lodash-es';
import {AbstractControl, FormArray, FormBuilder, FormGroup, Validators,} from '@angular/forms';
import {MarketplaceManagerService} from 'src/app/services/marketplace-manager.service';

@Component({
  selector: 'app-deploy-solution-form',
  template: `
    <div class="ed-head">
      <div class="t">
        <h2>Solution details</h2>
        <p>What consumers see in the marketplace · {{ data?.solution_name | textpretty | titlecase }}</p>
      </div>
      <span class="ed-x" (click)="closeDialogForm()"><mat-icon>close</mat-icon></span>
    </div>

    <div mat-dialog-content class="ed-body">
      <form [formGroup]="form">
        <mat-form-field class="full">
          <mat-label>Subtitle</mat-label>
          <input type="text" matInput formControlName="subtitle" required/>
        </mat-form-field>

        <mat-form-field class="full">
          <mat-label>Description</mat-label>
          <textarea matInput rows="3" formControlName="description" required></textarea>
        </mat-form-field>

        <mat-form-field class="full">
          <mat-label>Video link (optional)</mat-label>
          <input type="text" matInput formControlName="videoLink" placeholder="s3 bucket link"/>
        </mat-form-field>

        <mat-form-field class="full">
          <mat-label>Available to (deploy to consumer tenants)</mat-label>
          <mat-select formControlName="deploy_for" multiple>
            <mat-option value="ALL">ALL</mat-option>
            <mat-option [value]="tenant.name" *ngFor="let tenant of tenantNames">
              {{ tenant.name }}</mat-option>
          </mat-select>
          <mat-hint>Pick ALL for every consumer, or specific tenants.</mat-hint>
        </mat-form-field>

        <div class="arr-block" formArrayName="feature">
          <div class="arr-lbl">What you get (features)</div>
          <div class="arr-row" *ngFor="let feature of featuresFormArray.controls; let i = index">
            <mat-form-field class="grow">
              <mat-label>Feature</mat-label>
              <input type="text" matInput [formControlName]="i"/>
            </mat-form-field>
            <button type="button" class="rmv" (click)="removeFeature(i)">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <button type="button" class="btn-ghost" (click)="addFeature()">
            <mat-icon>add</mat-icon> Add feature
          </button>
        </div>

        <div class="arr-block" formArrayName="images">
          <div class="arr-lbl">Images</div>
          <div class="arr-row" *ngFor="let image of imagesFormArray.controls; let i = index">
            <mat-form-field class="grow">
              <mat-label>Image link</mat-label>
              <input type="text" matInput [formControlName]="i" placeholder="s3 bucket image link"/>
            </mat-form-field>
            <button type="button" class="rmv" (click)="removeImage(i)">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <button type="button" class="btn-ghost" (click)="addImage()">
            <mat-icon>add</mat-icon> Add image
          </button>
        </div>
      </form>
    </div>

    <div class="ed-foot">
      <button class="btn-ghost" (click)="closeDialogForm()">Cancel</button>
      <button class="btn-primary" (click)="saveForm()" [disabled]="!form.valid">
        Save details
      </button>
    </div>
  `,
  styleUrls: ['./deploy-solution-form.component.scss'],
})
export class DeploySolutionFormComponent implements OnInit {
  form: FormGroup | any;
  tenantNames: any = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<DeploySolutionFormComponent>,
    private dialog: MatDialog,
    private snackBar: SharedFeaturesService,
    private fb: FormBuilder,
    private marketPlaceManagerServices: MarketplaceManagerService
  ) {
  }

  get featuresFormArray() {
    return this.form.get('feature') as FormArray;
  }

  get imagesFormArray() {
    return this.form.get('images') as FormArray;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      description: ['', Validators.required],
      subtitle: ['', Validators.required],
      deploy_for: ['', Validators.required],
      videoLink: [''],
      feature: this.fb.array([], [this.validateArrayNotEmpty]),
      images: this.fb.array([], [this.validateArrayNotEmpty]),
    });
    if (this.data?.user_facing_solution_desc) {
      this.form.patchValue({
        ...this.data?.user_facing_solution_desc,
        deploy_for: this.data['deploy_for'],
      });
      this.patchFeatures(this.data?.user_facing_solution_desc?.feature);
      this.patchImages(this.data?.user_facing_solution_desc?.images);
    }
    this.marketPlaceManagerServices.getAvailableTenant().subscribe({
      next: (response: any) => {
        this.tenantNames = response?.msg;
      },
      error: (error: any) => {
        console.error('An error occurred', error);
      },
    });
  }

  patchFeatures(features: string[]) {
    const featureControls = this.form.get('feature') as FormArray;
    features.forEach((feature) => {
      featureControls.push(this.fb.control(feature));
    });
  }

  patchImages(images: string[]) {
    const imageControls = this.form.get('images') as FormArray;
    images.forEach((image) => {
      imageControls.push(this.fb.control(image));
    });
  }

  addFeature() {
    this.featuresFormArray.push(this.fb.control(''));
  }

  addImage() {
    this.imagesFormArray.push(this.fb.control(''));
  }

  removeFeature(index: number) {
    this.featuresFormArray.removeAt(index);
  }

  removeImage(index: number) {
    this.imagesFormArray.removeAt(index);
  }

  validateArrayNotEmpty(
    control: AbstractControl
  ): { [key: string]: any } | null {
    const values = control.value as string[];
    if (values.some((value) => value.trim() === '')) {
      return {arrayNotEmpty: true};
    }
    return null;
  }

  closeDialogForm() {
    if (!isEqual(this.data?.user_facing_solution_desc, this.form.value)) {
      const dialogRef = this.dialog.open(ShowDialogsComponent, {
        data: {
          title: 'Save your changes?',
          message: 'You have unsaved changes to the solution details.',
          confirmText: 'Save',
          cancelText: 'Discard',
        },
        disableClose: false,
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.saveForm();
        } else {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  saveForm() {
    if (this.form.valid) {
      const formOutput: any = this.form.value;
      this.dialogRef.close(formOutput);
    } else {
      this.snackBar.openSnackBar(
        'Invalid form input',
        'Close',
        'error-snackbar'
      );
    }
  }
}
